import React from "react";

import _ from "lodash";
import { FormControl, type FormControlProps, FormLabel } from "react-bootstrap";
import {
  type Control,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type UseControllerProps,
} from "react-hook-form";

import { useScopedController } from "@/components/ScopedForm/hooks";
import RHErrorMessage from "@/containers/RHErrorMessage";
import TTFormGroup from "@/containers/TTFormGroup";

export interface RHInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>
  extends UseControllerProps<TFieldValues, TName, TTransformedValues>, Omit<FormControlProps, "defaultValue" | "name"> {
  control: Control<TFieldValues, any, TTransformedValues>;

  children?: React.ReactNode;
  validate?: boolean;
  isValid?: boolean;
  isInvalid?: boolean;

  hideError?: boolean;
  namespace?: string;
  errorClassName?: string;
  containerClassName?: string;

  label?: string | React.ReactNode;
  labelClassName?: string;

  fallBackOnNull?: boolean;
}

const RH_PROPS = ["rules", "control", "defaultValue", "value", "defaultValue", "shouldUnregister", "disabled"];

export default function RHInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  validate = true,
  isInvalid,
  isValid,
  id,
  label,
  labelClassName,
  hideError = false,
  namespace,
  errorClassName,
  containerClassName,
  fallBackOnNull,
  onChange: outerOnChange,
  ...props
}: RHInputProps<TFieldValues, TName, TTransformedValues>) {
  const {
    field,
    formState: { isSubmitted },
    fieldState: { invalid, isTouched, error },
  } = useScopedController(_.omit(props, ["disabled"]));

  const cleanProps = _.omit(props, RH_PROPS);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (props.type === "number") {
      field.onChange(e.target.value === "" ? "" : e.target.valueAsNumber);
    } else {
      field.onChange(e);
    }

    if (outerOnChange) {
      outerOnChange(e);
    }
  }

  let value = field.value;
  if (fallBackOnNull && value === null) {
    value = "" as unknown as FieldPathValue<TFieldValues, TName>;
  }

  if (label && id) {
    return (
      <TTFormGroup className={containerClassName}>
        <FormLabel htmlFor={id} className={labelClassName}>
          {label}
        </FormLabel>

        <FormControl
          {...cleanProps}
          {...field}
          onChange={onChange}
          value={value}
          id={id}
          isValid={isValid ?? (validate && !invalid && (isTouched || isSubmitted))}
          isInvalid={isInvalid ?? (validate && invalid && (isTouched || isSubmitted))}
        />

        {validate && !hideError && (
          <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
        )}
      </TTFormGroup>
    );
  }

  return (
    <>
      <FormControl
        {...cleanProps}
        {...field}
        onChange={onChange}
        value={value}
        id={id}
        isValid={isValid ?? (validate && !invalid && (isTouched || isSubmitted))}
        isInvalid={isInvalid ?? (validate && invalid && (isTouched || isSubmitted))}
      />

      {validate && !hideError && (
        <RHErrorMessage namespace={namespace} errorMessage={error} className={errorClassName} />
      )}
    </>
  );
}
